import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, MoreHorizontal, Plus, Send, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";

import { DatePick, DueDateTime, TimePick } from "../../components/DueDateTime";
import { Button } from "../../components/ui/button";
import { FilterChip } from "../../components/ui/primitives";
import { byPeople, DEPT_NAME, editorsOfProject, GROUP_OF, homeProject, ME_CLIENT, ME_EDITOR, PEOPLE, PROJECTS, projectPublishesAfterModeration, projectsOf, rememberProject, type Dept, type PersonId } from "../../data/people";
import { instagramPublicationTarget, useProjectAccounts } from "../../lib/accounts";
import { defaultDue, freestOn, PUB_FORMATS } from "../../lib/domain";
import { knownFormat, readDirectionPrefs, rememberDirectionPrefs } from "../../lib/directionPrefs";
import { easeOut } from "../../lib/motion";
import { notify } from "../../lib/notify";
import { at, dayInfo, dayOf, DAYS } from "../../lib/time";
import { useNow } from "../../lib/useNow";
import { cn, plural, uid } from "../../lib/utils";
import { boardHides, clearBoardFilters, useStore } from "../../store";

/**
 * Несколько публикаций за раз (09.10.2026, просьба Артура): ролики на канал
 * планируют сразу на неделю, а окно «одна публикация» заставляло проходить
 * два шага на каждую.
 *
 * Каждая строка — отдельная публикация со своим названием, форматом,
 * аккаунтом, монтажёром и сроком. Направление одно на всю пачку: от него
 * зависят списки аккаунтов и монтажёров в строках.
 */

type Row = {
  key: string;
  title: string;
  format: string;
  accountId: string | null;
  /** "" — без исполнителя */
  editor: PersonId | "";
  due: number;
  brief: string;
  open: boolean;
};

/** Дата «всем сразу» на сегодня, а время уже прошло — ближайшие 15 минут. */
const keepFuture = (due: number, now: number) => (due > now ? due : Math.ceil((now + 1) / 15) * 15);

const SELECT = "relative inline-flex h-8 min-w-0 items-center gap-1 rounded-full border bg-background pl-3 pr-2 text-[13px] font-medium hover:bg-muted";
const fold = { duration: 0.24, ease: easeOut } as const;

function Pick<T extends string>({ value, options, onChange, className, placeholder }: {
  value: T | "";
  options: { value: T | ""; label: string }[];
  onChange: (v: T | "") => void;
  className?: string;
  placeholder?: string;
}) {
  const current = options.find((o) => o.value === value);
  return (
    <label className={cn(SELECT, className)}>
      <span className="truncate">{current?.label ?? placeholder ?? "—"}</span>
      <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
      <select value={value} onChange={(e) => onChange(e.target.value as T | "")} className="absolute inset-0 cursor-pointer opacity-0">
        {placeholder && !current && <option value={value}>{placeholder}</option>}
        {options.map((o) => <option key={o.value || "none"} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

export function BatchNewTasks({ dept, first, own, onClose, onCreated }: {
  dept?: Dept;
  /** что уже успели ввести в окне одной публикации */
  first?: { title: string; format: string; brief: string };
  /**
   * Монтажёр ставит себе сам («Своя публикация»): колонки «Монтажёр» нет,
   * направления — те, где он состоит (09.10.2026: пачкой — для всех ролей).
   */
  own?: boolean;
  onClose: () => void;
  /** поставленные id — «Своя публикация» открывает единственную на сдаче */
  onCreated?: (ids: string[]) => void;
}) {
  const now = useNow();
  const tasks = useStore((s) => s.tasks);
  const busy = useStore((s) => s.busy);
  const createTask = useStore((s) => s.createTask);
  const cancelTask = useStore((s) => s.cancelTask);

  const me = own ? ME_EDITOR : ME_CLIENT;
  const group = GROUP_OF[me];
  const projects = own
    ? PROJECTS.filter((p) => (byPeople(p) ? p.editorIds!.includes(ME_EDITOR) : Boolean(group && p.groupIds.includes(group))))
    : projectsOf(dept!);
  const [projectId, setProjectId] = useState<string | null>(() => homeProject(projects, group)?.id ?? projects[0]?.id ?? null);
  const accounts = useProjectAccounts(projectId);
  // Прошлый выбор в направлении (02.11.2026): аккаунт, формат, автопубликация.
  const prefs = readDirectionPrefs(projectId);
  const prefAccount = prefs.accountIds?.find((id) => accounts.some((a) => a.id === id)) ?? accounts[0]?.id ?? null;
  const editors = own ? [ME_EDITOR] : projectId ? editorsOfProject(projectId) : [];

  const today = dayOf(now);
  const startDay = DAYS.find((d) => d.idx >= today && defaultDue(d.idx, now) > now)?.idx ?? DAYS[0].idx;

  const blank = (patch: Partial<Row> = {}): Row => ({
    key: uid(),
    title: "",
    format: knownFormat(prefs.format),
    accountId: prefAccount,
    editor: editors.length ? freestOn(startDay, tasks, busy, editors) : "",
    due: defaultDue(startDay, now),
    brief: "",
    open: false,
    ...patch,
  });
  // «Один ролик на N каналов» с 02.11.2026 ставится прямо из окна одной
  // публикации — сюда его больше не перекидывает.
  const [rows, setRows] = useState<Row[]>(() => [blank({ title: first?.title ?? "", format: first?.format ?? knownFormat(prefs.format), brief: first?.brief ?? "", open: Boolean(first?.brief) })]);
  // В «Президенте» после «Норм» ролик всегда уходит в Instagram сам — галочку не снять (28.10.2026).
  const [autoPublishChoice, setAutoPublishInstagram] = useState<boolean | null>(prefs.autoPublish ?? null);
  const afterModeration = projectPublishesAfterModeration(projectId);
  const autoPublishInstagram = afterModeration || (autoPublishChoice ?? false);
  const [tried, setTried] = useState(false);
  const list = useRef<HTMLDivElement>(null);

  const update = (key: string, patch: Partial<Row>) => setRows((l) => l.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const remove = (key: string) => setRows((l) => (l.length > 1 ? l.filter((r) => r.key !== key) : l));

  /** Следующая — копия последней: тот же аккаунт, монтажёр и время, на день позже. */
  const add = (title = "") => {
    setRows((l) => {
      const last = l[l.length - 1];
      return [...l, { ...blank(), ...(last ? { format: last.format, accountId: last.accountId, editor: last.editor, due: last.due + 1440 } : {}), key: uid(), title, brief: "", open: false }];
    });
    requestAnimationFrame(() => {
      const el = list.current;
      el?.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      el?.querySelectorAll<HTMLInputElement>("input[data-title]")?.[el.querySelectorAll("input[data-title]").length - 1]?.focus();
    });
  };

  /** Вставили столбик названий — по строке на каждое. */
  const paste = (key: string, text: string): boolean => {
    const lines = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (lines.length < 2) return false;
    setRows((l) => {
      const i = l.findIndex((r) => r.key === key);
      const base = l[i];
      const fresh = lines.slice(1).map((title, n) => ({ ...base, key: uid(), title, brief: "", open: false, due: base.due + 1440 * (n + 1) }));
      return [...l.slice(0, i), { ...base, title: lines[0] }, ...fresh, ...l.slice(i + 1)];
    });
    return true;
  };

  const changeProject = (id: string) => {
    setProjectId(id);
    setAutoPublishInstagram(readDirectionPrefs(id).autoPublish ?? null);
    // Аккаунты и монтажёры у направления свои — строки переводим на прошлые
    // аккаунты этого направления (accountOf) или его первые.
    const nextEditors = own ? [ME_EDITOR] : editorsOfProject(id);
    setRows((l) => l.map((r) => ({ ...r, accountId: null, editor: r.editor && nextEditors.includes(r.editor) ? r.editor : nextEditors.length ? freestOn(dayOf(r.due), tasks, busy, nextEditors) : "" })));
  };

  // Аккаунт по умолчанию — прошлый в этом направлении, иначе первый у него.
  const accountOf = (r: Row) => r.accountId ?? prefAccount;
  const instagramAccounts = accounts.filter((account) => account.platform === "instagram" && rows.some((row) => accountOf(row) === account.id));
  const publicationTarget = instagramPublicationTarget(instagramAccounts);

  const problems = useMemo(() => rows.map((r) => (r.title.trim().length < 3 ? "название" : r.due <= now ? "срок прошёл" : null)), [rows, now]);
  const ready = rows.length > 0 && problems.every((p) => !p) && !!projectId;

  const applyAll = (patch: Partial<Row> | ((r: Row) => Partial<Row>)) =>
    setRows((l) => l.map((r) => ({ ...r, ...(typeof patch === "function" ? patch(r) : patch) })));

  const submit = () => {
    setTried(true);
    if (!ready || !projectId) return;
    rememberProject(projectId);
    const used = [...new Set(rows.map(accountOf).filter((x): x is string => Boolean(x)))];
    rememberDirectionPrefs(projectId, { accountIds: used, format: rows[rows.length - 1].format, autoPublish: autoPublishChoice ?? undefined });
    const taskDept = own ? (DEPT_NAME[projects.find((p) => p.id === projectId)?.divisionId ?? ""] ?? PEOPLE[ME_EDITOR].dept ?? "") : dept!;
    const ids = rows.map((r) =>
      createTask({
        title: r.title.trim(),
        description: r.brief.trim(),
        period: "day",
        dept: taskDept,
        projectId,
        autoPublishInstagram,
        items: [{ id: uid(), title: r.title.trim(), format: r.format, brief: r.brief.trim(), accountId: accountOf(r), files: [] }],
        creator: me,
        editor: own ? ME_EDITOR : r.editor,
        day: dayOf(r.due),
        estimate: 2,
        due: r.due,
      }),
    );
    onClose();
    onCreated?.(ids);
    // Поиск и фильтры доски не сбрасываем: прячут новые карточки — «Показать».
    const hidden = rows.some((r) => boardHides({ title: r.title.trim(), projectId, editor: own ? ME_EDITOR : r.editor }));
    const days = [...new Set(rows.map((r) => dayOf(r.due)))].sort((a, b) => a - b).map((d) => dayInfo(d).date);
    notify(`${ids.length} ${plural(ids.length, ["публикация поставлена", "публикации поставлены", "публикаций поставлено"])} · ${days.join(", ")} ${dayInfo(dayOf(rows[rows.length - 1].due)).month}`, {
      icon: "send",
      undo: () => ids.forEach((id) => cancelTask(id)),
      action: hidden && !own ? { label: "Показать", run: clearBoardFilters } : undefined,
    });
  };

  const cols = own ? "grid-cols-[24px_minmax(0,1fr)_120px_190px_250px_64px]" : "grid-cols-[24px_minmax(0,1fr)_120px_190px_160px_250px_64px]";
  const accountOptions = accounts.map((a) => ({ value: a.id, label: a.name }));
  const editorOptions = [...editors.map((e) => ({ value: e, label: PEOPLE[e].name })), { value: "" as const, label: "Без исполнителя" }];

  return (
    <>
      <div className="flex shrink-0 items-start gap-3 pb-1 pl-7 pr-5 pt-6">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 className="text-[22px] font-semibold tracking-[-0.02em]">{own ? "Свои публикации" : "Несколько публикаций"}</h2>
          <p className="text-sm text-muted-foreground">
            Каждая строка — отдельная публикация. Вставьте столбик названий — получится по строке на каждое.
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} title="Закрыть (Esc)">
          <X className="!size-[18px]" />
        </Button>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-3 px-7 pb-3 pt-3">
        <span className="text-sm font-medium">Направление</span>
        {projects.length ? projects.map((p) => (
          <FilterChip key={p.id} active={p.id === projectId} onClick={() => changeProject(p.id)}>{p.name}</FilterChip>
        )) : <span className="text-[13px] text-muted-foreground">Вы не состоите ни в одном направлении — попросите руководителя отдела.</span>}
      </div>

      <div ref={list} className="scroll-thin flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-7 pb-3">
        <div className={cn("grid items-center gap-2 px-[9px] text-xs font-medium text-muted-foreground", cols)}>
          <span />
          <span>Название</span>
          <span>Формат</span>
          <span>Аккаунт</span>
          {!own && <span>Монтажёр</span>}
          <span>Срок</span>
          <span />
        </div>
        <AnimatePresence initial={false}>
          {rows.map((r, i) => {
            const problem = tried ? problems[i] : null;
            return (
              <motion.div
                key={r.key}
                layout
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={fold}
                className="overflow-hidden"
              >
                <div className={cn("flex flex-col gap-2 rounded-[18px] border bg-background p-2", problem ? "border-rework" : "border-border")}>
                  <div className={cn("grid items-center gap-2", cols)}>
                    <span className="text-center text-xs tabular-nums text-muted-foreground">{i + 1}</span>
                    <input
                      data-title
                      value={r.title}
                      autoFocus={i === 0}
                      onChange={(e) => update(r.key, { title: e.target.value })}
                      onPaste={(e) => { if (paste(r.key, e.clipboardData.getData("text"))) e.preventDefault(); }}
                      onKeyDown={(e) => { if (e.key === "Enter" && i === rows.length - 1) { e.preventDefault(); add(); } }}
                      placeholder={i === 0 ? "Например, Токаев и флаг" : "Название"}
                      className="h-8 min-w-0 rounded-full border border-input bg-background px-3 text-[13px] outline-none placeholder:text-muted-foreground focus:ring-4 focus:ring-ring/20"
                    />
                    <Pick value={r.format} options={PUB_FORMATS.map((f) => ({ value: f.value, label: f.label }))} onChange={(v) => update(r.key, { format: v || PUB_FORMATS[0].value })} />
                    <Pick value={accountOf(r) ?? ""} options={accountOptions} placeholder="нет аккаунтов" onChange={(v) => update(r.key, { accountId: v || null })} />
                    {!own && (
                      <Pick
                        value={r.editor}
                        options={editorOptions}
                        onChange={(v) => update(r.key, { editor: v as PersonId | "" })}
                        className={!r.editor ? "text-muted-foreground" : undefined}
                      />
                    )}
                    <span className="flex items-center gap-1.5 whitespace-nowrap">
                      <DueDateTime due={r.due} now={now} onChange={(v) => update(r.key, { due: v })} />
                    </span>
                    <span className="flex items-center justify-end gap-0.5">
                      <button type="button" title="Бриф" onClick={() => update(r.key, { open: !r.open })} className={cn("grid size-7 place-items-center rounded-full hover:bg-muted", (r.open || r.brief) && "text-primary")}>
                        <MoreHorizontal className="size-4" />
                      </button>
                      <button type="button" title="Убрать" disabled={rows.length === 1} onClick={() => remove(r.key)} className="grid size-7 place-items-center rounded-full text-muted-foreground hover:bg-muted disabled:opacity-30">
                        <X className="size-4" />
                      </button>
                    </span>
                  </div>
                  {problem && (
                    <div className="pl-8 text-xs text-rework-fg">{problem === "название" ? "Назовите публикацию" : "Срок уже прошёл"}</div>
                  )}
                  {r.open && (
                    <textarea
                      value={r.brief}
                      maxLength={2000}
                      autoFocus
                      onChange={(e) => update(r.key, { brief: e.target.value })}
                      rows={2}
                      placeholder="Что снять: хук, что обязательно показать, чего избегать…"
                      className="ml-8 resize-none rounded-[14px] border border-input bg-background px-3 py-2 text-[13px] outline-none placeholder:text-muted-foreground focus:ring-4 focus:ring-ring/20"
                    />
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
        <button type="button" onClick={() => add()} className="flex h-10 items-center gap-2 self-start rounded-full px-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
          <Plus className="size-4" />
          Ещё публикация
          <span className="text-xs font-normal">— на день позже, остальное как в последней</span>
        </button>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-t px-7 py-4">
        {instagramAccounts.length > 0 && (
          <label className="mr-3 flex cursor-pointer items-center gap-2 text-[13px]">
            <input type="checkbox" checked={autoPublishInstagram} disabled={afterModeration} onChange={(e) => setAutoPublishInstagram(e.target.checked)} className="size-4 accent-primary disabled:opacity-100" />
            Автоматически опубликовать в {publicationTarget}{afterModeration ? " после модерации" : ""}
          </label>
        )}
        <span className="text-[13px] text-muted-foreground">Всем сразу:</span>
        <Pick value="" options={accountOptions} placeholder="аккаунт" onChange={(v) => v && applyAll({ accountId: v })} />
        {!own && <Pick
          value=""
          options={editorOptions.map((o) => ({ value: o.value || "free", label: o.label }))}
          placeholder="монтажёр"
          onChange={(v) => v && applyAll({ editor: v === "free" ? "" : (v as PersonId) })}
        />}
<DatePick day={null} now={now} placeholder="дата" onPick={(d) => applyAll((r) => ({ due: keepFuture(at(d, 0, r.due - dayOf(r.due) * 1440), now) }))} />
        <TimePick minutes={null} now={now} placeholder="время" onPick={(m) => applyAll((r) => ({ due: at(dayOf(r.due), 0, m) }))} />
        <span className="flex-1" />
        {tried && !ready && <span className="text-[13px] text-rework-fg">Проверьте выделенные строки</span>}
        <Button onClick={submit} disabled={!projectId}>
          <Send />
          Поставить {rows.length} {plural(rows.length, ["публикацию", "публикации", "публикаций"])}
        </Button>
      </div>
    </>
  );
}
